<script setup lang="ts">
/* =============================================================
   COMPANY — DETAIL
   =============================================================
   Every field on the row, plus how many berths and vessels the
   company has, and the only place a company can be edited or
   deleted from. See the note in BerthDetailPage.

   Deleting a company that still has users fails with 23503 —
   profiles.company_id is `on delete restrict` — and the confirm
   dialog reports that rather than closing as if it worked.
   ============================================================= */

import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import DetailList from '@/components/ui/DetailList.vue'
import type { DetailRow } from '@/components/ui/DetailList.vue'
import { routes, to } from '@/config/routes'
import { deleteCompany, getCompanyWithCounts } from '@/lib/companies'
import { useCountries } from '@/lib/countries'
import { DataError } from '@/lib/errors'
import type { CompanyWithCounts } from '@/lib/types'

const route = useRoute()
const router = useRouter()
const { countryName } = useCountries()

const company = ref<CompanyWithCounts | null>(null)
const loading = ref(true)
const loadError = ref('')

const id = computed(() => Number(route.params.id))

async function load(): Promise<void> {
  loading.value = true
  loadError.value = ''
  try {
    company.value = await getCompanyWithCounts(id.value)
  } catch (e) {
    loadError.value = e instanceof DataError ? e.message : 'Could not load the company.'
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch(id, load)

const rows = computed<DetailRow[]>(() => {
  const c = company.value
  if (!c) return []
  return [
    { label: 'Name', value: c.name },
    { label: 'Country', value: `${countryName(c.country_code)} (${c.country_code})` },
    // Counted by the database on the same request that fetched the row — see
    // listCompaniesWithCounts. Zero is a real answer: most of these companies
    // own berths and operate nothing.
    { label: 'Berths', value: String(c.berths), mono: true },
    { label: 'Vessels', value: String(c.vessels), mono: true },
    { label: 'VAT number', value: c.vat_number ?? '', empty: !c.vat_number, mono: !!c.vat_number },
    { label: 'Contact email', value: c.contact_email, href: `mailto:${c.contact_email}` },
    // `href`, not `to`: this leaves the panel. The stored value always carries
    // its scheme (the CHECK requires it), so it needs no fixing up here.
    c.website
      ? { label: 'Website', value: c.website, href: c.website }
      : { label: 'Website', empty: true },
    { label: 'ID', value: String(c.id), mono: true },
    { label: 'Created', value: new Date(c.created_at).toLocaleString('en-GB') },
  ]
})

const confirming = ref(false)
const confirm = useTemplateRef<InstanceType<typeof ConfirmDialog>>('confirm')

async function onDeleteConfirmed(): Promise<void> {
  const c = company.value
  if (!c) return
  try {
    await deleteCompany(c.id)
    confirming.value = false
    await router.push(routes.companies)
  } catch (e) {
    confirm.value?.fail(e instanceof DataError ? e.message : 'Could not delete the company.')
  }
}
</script>

<template>
  <p v-if="loading" class="page-status">Loading…</p>

  <template v-else-if="company">
    <PageHeader :title="company.name" :description="countryName(company.country_code)">
      <template #actions>
        <Button variant="ghost" icon="chevronRight" :to="routes.companies" class="back-link">
          All companies
        </Button>
        <Button variant="secondary" icon="edit" :to="to.companyEdit(company.id)">Edit</Button>
        <Button variant="danger" icon="trash" @click="confirming = true">Delete</Button>
      </template>
    </PageHeader>

    <Panel>
      <DetailList :rows="rows" />
    </Panel>

    <ConfirmDialog
      ref="confirm"
      :open="confirming"
      title="Delete company"
      :message="`“${company.name}” will be permanently removed. A company that still has users cannot be deleted.`"
      @close="confirming = false"
      @confirmed="onDeleteConfirmed"
    />
  </template>

  <template v-else>
    <Alert v-if="loadError" tone="danger" class="page-alert">{{ loadError }}</Alert>
    <EmptyState
      v-else
      icon="building"
      title="Company not found"
      description="It may have been deleted, or the link may be wrong."
    >
      <template #action>
        <Button :to="routes.companies">All companies</Button>
      </template>
    </EmptyState>
  </template>
</template>
